import { invariant } from "@x1-error/invariant";

const DARK_MODE_CLASS_NAME = "__x1-dark-mode";
const LIGHT_MODE_CLASS_NAME = "__x1-light-mode";

function buildTheme(selector, theme) {
  const lines = [];
  lines.push(`${selector} {`);

  for (const key in theme) {
    const value = theme[key];
    lines.push(`  --${key}: ${value};`);
  }

  lines.push("}");

  return lines.join("\n");
}

function makeStyleTag() {
  const tag = document.createElement("style");
  tag.setAttribute("type", "text/css");
  tag.setAttribute("data-stylex", "true");

  const head = document.head || document.getElementsByTagName("head")[0];
  head || invariant(0, 2655);
  head.appendChild(tag);

  return tag;
}

function doesSupportCSSVariables() {
  return (
    window.CSS && window.CSS.supports && window.CSS.supports("--fake-var:0")
  );
}

const VARIABLE_MATCH = /var\(--(.*?)\)/g;

export class StyleXSheet {
  static DARK_MODE_CLASS_NAME = DARK_MODE_CLASS_NAME;
  static LIGHT_MODE_CLASS_NAME = LIGHT_MODE_CLASS_NAME;

  constructor(opts) {
    this.tag = null;
    this.injected = false;
    this.ruleForPriority = new Map();
    this.rules = [];
    this.rootTheme = opts.rootTheme;
    this.rootDarkTheme = opts.rootDarkTheme;
    this.supportsVariables =
      opts.supportsVariables ?? doesSupportCSSVariables();
  }

  isHeadless() {
    return !this.tag || !window?.document?.body;
  }

  getTag() {
    const { tag } = this;
    tag || invariant(0, 11103);
    return tag;
  }

  getRuleCount() {
    return this.rules.length;
  }

  inject() {
    if (this.injected) {
      return;
    }

    this.injected = true;

    if (!window.document?.body) {
      this.injectTheme();
      return;
    }

    this.tag = makeStyleTag();
    this.injectTheme();
  }

  injectTheme() {
    if (this.rootTheme) {
      this.insert(
        buildTheme(`:root, .${LIGHT_MODE_CLASS_NAME}`, this.rootTheme),
        0,
      );
    }
    if (this.rootDarkTheme) {
      this.insert(
        buildTheme(
          `.${DARK_MODE_CLASS_NAME}:root, .${DARK_MODE_CLASS_NAME}`,
          this.rootDarkTheme,
        ),
        0,
      );
    }
  }

  normalizeRule(rule) {
    const { rootTheme } = this;
    if (this.supportsVariables || !rootTheme) {
      return rule;
    }

    return rule.replace(VARIABLE_MATCH, (_match, name) => {
      return rootTheme[name];
    });
  }

  getInsertPositionForPriority(priority) {
    const priorityRule = this.ruleForPriority.get(priority);
    if (priorityRule) {
      return this.rules.indexOf(priorityRule) + 1;
    }

    const priorities = Array.from(this.ruleForPriority.keys())
      .sort((a, b) => b - a)
      .filter((num) => (num > priority ? 1 : 0));

    if (priorities.length === 0) {
      return this.getRuleCount();
    }

    const lastPriority = priorities.pop();
    return this.rules.indexOf(this.ruleForPriority.get(lastPriority));
  }

  insert(rawLTRRule, priority, rawRTLRule) {
    if (this.injected === false) {
      this.inject();
    }

    if (rawRTLRule) {
      this.insert(
        addAncestorSelector(rawLTRRule, "html:not([dir='rtl'])"),
        priority,
      );
      this.insert(addAncestorSelector(rawRTLRule, "html[dir='rtl']"), priority);
      return;
    }

    const rawRule = rawLTRRule;

    if (this.rules.includes(rawRule)) {
      return;
    }

    const rule = this.normalizeRule(
      addSpecificityLevel(rawRule, Math.floor(priority / 1000)),
    );

    const insertPos = this.getInsertPositionForPriority(priority);
    this.rules.splice(insertPos, 0, rule);

    this.ruleForPriority.set(priority, rule);

    if (this.isHeadless()) {
      return;
    }

    const tag = this.getTag();
    const sheet = tag.sheet;

    if (sheet) {
      try {
        sheet.insertRule(rule, Math.min(insertPos, sheet.cssRules.length));
      } catch (err) {
        console.error("insertRule error", err, rule, insertPos);
      }
    }
  }
}

function addAncestorSelector(selector, ancestorSelector) {
  if (!selector.startsWith("@")) {
    return `${ancestorSelector} ${selector}`;
  }

  const firstBracketIndex = selector.indexOf("{");
  const mediaQueryPart = selector.slice(0, firstBracketIndex + 1);
  const rest = selector.slice(firstBracketIndex + 1);
  return `${mediaQueryPart}${ancestorSelector} ${rest}`;
}

function addSpecificityLevel(selector, index) {
  if (selector.startsWith("@keyframes")) {
    return selector;
  }
  const pseudo = Array.from({ length: index })
    .map(() => ":not(#\\#)")
    .join("");

  const lastOpenCurly = selector.includes("::")
    ? selector.indexOf("::")
    : selector.lastIndexOf("{");
  const beforeCurly = selector.slice(0, lastOpenCurly);
  const afterCurly = selector.slice(lastOpenCurly);

  return `${beforeCurly}${pseudo}${afterCurly}`;
}
